'use client';

import { useCallback, useEffect } from 'react';
import type { IProductListItem } from '@/api/types/product';
import type { CampaignsServerData } from '../../types';
import CampaignBanner from '../../components/CampaignBanner';
import { useCampaignPageTracking } from '../../hooks/useCampaignPageTracking';
import PromotionBottomBanner from './components/PromotionBottomBanner';
import PromotionFlashSaleSection from './components/PromotionFlashSaleSection';
import PromotionProductSection from './components/PromotionProductSection';

interface PromotionPageProps {
  serverData: CampaignsServerData;
  isAppCommunicationReady: boolean;
  inAppH5: boolean;
  onBottomContentReady?: () => void;
}

export default function PromotionPage({
  serverData,
  isAppCommunicationReady,
  inAppH5,
  onBottomContentReady,
}: PromotionPageProps) {
  const promotionData = serverData.promotionData;
  const pageData = promotionData?.pageData;
  const {
    trackPageView,
    trackPageLeave,
    trackProductListPitsClick,
    trackProductListPitsExposure,
    trackAdClick,
    trackAdExp,
  } = useCampaignPageTracking(serverData.runtimeConfig);

  useEffect(() => {
    if (isAppCommunicationReady) {
      onBottomContentReady?.();
    }
  }, [isAppCommunicationReady, onBottomContentReady]);

  useEffect(() => {
    if (!isAppCommunicationReady) return;

    trackPageView();

    if (inAppH5) {
      window.addEventListener('appView', trackPageView);
      window.addEventListener('appLeave', trackPageLeave);
    }

    return () => {
      trackPageLeave();
      if (inAppH5) {
        window.removeEventListener('appView', trackPageView);
        window.removeEventListener('appLeave', trackPageLeave);
      }
    };
  }, [inAppH5, isAppCommunicationReady, trackPageLeave, trackPageView]);

  const trackPromotionProductClick = useCallback(
    (item: IProductListItem, index: number) => {
      trackProductListPitsClick(item, index, '促销区');
    },
    [trackProductListPitsClick],
  );

  const trackPromotionProductExposure = useCallback(
    (item: IProductListItem, index: number) => {
      trackProductListPitsExposure(item, index, '促销区');
    },
    [trackProductListPitsExposure],
  );

  const trackFlashSaleProductClick = useCallback(
    (item: IProductListItem, index: number) => {
      trackProductListPitsClick(item, index, '限购区');
    },
    [trackProductListPitsClick],
  );

  const trackFlashSaleProductExposure = useCallback(
    (item: IProductListItem, index: number) => {
      trackProductListPitsExposure(item, index, '限购区');
    },
    [trackProductListPitsExposure],
  );

  const getBannerTrackProperties = useCallback((item: any) => {
    return {
      pits_jump_type: `${item.jump_content?.jump_type || ''}`,
      pits_jump_content: `${item.jump_content?.Jump_target || ''}`,
    };
  }, []);

  const trackBottomBannerClick = useCallback(
    (item: any, index: number) => {
      trackAdClick('底部banner位', index, undefined, getBannerTrackProperties(item));
    },
    [getBannerTrackProperties, trackAdClick],
  );

  const trackBottomBannerExposure = useCallback(
    (item: any, index: number) => {
      trackAdExp('底部banner位', '', index, getBannerTrackProperties(item));
    },
    [getBannerTrackProperties, trackAdExp],
  );

  if (!pageData || !isAppCommunicationReady) {
    return null;
  }

  return (
    <div className="bg-[#f4f4f4]">
      {pageData.banner && (
        <CampaignBanner
          data={{
            image: pageData.banner.image,
            image_alt: pageData.banner.image_alt,
            type: pageData.banner.type,
            jump_content: pageData.banner.jump_content,
          }}
          aspectRatio={pageData.banner.aspectRatio}
        />
      )}

      {pageData.flash_sale?.config && pageData.flash_sale.products.length > 0 && (
        <PromotionFlashSaleSection
          pageId={Number(serverData.runtimeConfig.marketing_page_id || 0)}
          config={pageData.flash_sale.config}
          newCustomerConfig={pageData.flash_sale.new_customer_config}
          oldCustomerConfig={pageData.flash_sale.old_customer_config}
          newUserRefId={pageData.flash_sale.new_user_ref_id}
          oldUserRefId={pageData.flash_sale.old_user_ref_id}
          products={pageData.flash_sale.products}
          customerSegment={pageData.flash_sale.customer_segment}
          countdownEndAt={pageData.flash_sale.countdown_end_at}
          remainingSeconds={pageData.flash_sale.remaining_seconds}
          soldOutSort={pageData.flash_sale.sold_out_sort}
          soldOutDisplay={pageData.flash_sale.sold_out_display}
          deviceType={serverData.deviceType}
          adp={serverData.adp}
          onProductClickTrack={trackFlashSaleProductClick}
          onProductExposureTrack={trackFlashSaleProductExposure}
          refreshOnMount={inAppH5}
        />
      )}

      {pageData.promotion && promotionData.promotionList.length > 0 && (
        <PromotionProductSection
          pageId={Number(serverData.runtimeConfig.marketing_page_id || 0)}
          groupId={pageData.promotion.group_id || ''}
          titleImage={pageData.promotion.title_image}
          titleAlt={pageData.promotion.title_image_alt}
          products={promotionData.promotionList}
          deviceType={serverData.deviceType}
          limit={promotionData.promotionLimit}
          initialHasMore={promotionData.promotionHasMore}
          adp={serverData.adp}
          refreshOnMount={inAppH5}
          onProductClickTrack={trackPromotionProductClick}
          onProductExposureTrack={trackPromotionProductExposure}
        />
      )}

      {pageData.bottom_banner.length > 0 && (
        <PromotionBottomBanner
          bottomBannerData={pageData.bottom_banner}
          deviceType={serverData.deviceType}
          onBannerClickTrack={trackBottomBannerClick}
          onBannerExposureTrack={trackBottomBannerExposure}
        />
      )}
    </div>
  );
}
